import {
  useRef,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';

export type KeisukeFreeLegendDragOverlayProps = {
  containerRef: RefObject<HTMLElement | null>;
  offsetXPct: number;
  offsetYPct: number;
  onOffsetsChange: (xPct: number, yPct: number) => void;
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  /** スクリーンリーダー用（例: 凡例をドラッグで移動） */
  dragLabel?: string;
};

/**
 * 「自由」凡例: グラフ領域（containerRef）に対する % 位置。ポインタでドラッグして更新する。
 */
export default function KeisukeFreeLegendDragOverlay({
  containerRef,
  offsetXPct,
  offsetYPct,
  onOffsetsChange,
  children,
  style,
  className,
  dragLabel,
}: KeisukeFreeLegendDragOverlayProps) {
  const dragOffsetRef = useRef({ dx: 0, dy: 0 });

  const clamp = (n: number) => Math.max(0, Math.min(100, n));

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    const leg = el.getBoundingClientRect();
    dragOffsetRef.current = { dx: e.clientX - leg.left, dy: e.clientY - leg.top };
    el.setPointerCapture(e.pointerId);
    el.style.cursor = 'grabbing';
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!el.hasPointerCapture(e.pointerId)) return;
    const container = containerRef.current;
    if (!container) return;
    const r = container.getBoundingClientRect();
    const w = Math.max(r.width, 1);
    const h = Math.max(r.height, 1);
    const leftPx = e.clientX - dragOffsetRef.current.dx - r.left;
    const topPx = e.clientY - dragOffsetRef.current.dy - r.top;
    onOffsetsChange(clamp((leftPx / w) * 100), clamp((topPx / h) * 100));
  };

  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    el.style.cursor = 'grab';
  };

  const x = clamp(Number(offsetXPct) || 0);
  const y = clamp(Number(offsetYPct) || 0);

  return (
    <div
      role="group"
      className={className}
      aria-label={dragLabel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        zIndex: 2,
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
        maxWidth: 'min(92%, 280px)',
        padding: '4px 8px',
        background: 'rgba(255, 255, 255, 0.92)',
        borderRadius: 4,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
